import {
    Table, TableHeader, TableRow, createTableColumn, TableHeaderCell, TableBody, TableCell, TableSelectionCell, useTableFeatures, useTableSort, TableColumnId,
    TableColumnSizingOptions, Menu, MenuTrigger, MenuPopover, MenuItem, MenuList, useTableColumnSizing_unstable, TableCellLayout, useTableSelection
} from '@fluentui/react-components';
import { INotification } from './insightsDashboard';
import moment from 'moment';
import React, { useState } from 'react';

export interface IInsightsTableProps {
    items: INotification[];
    onClick: (item: INotification) => Promise<void>;
    onDbClick: (item: INotification) => void
}

const InsightsTable: React.FC<IInsightsTableProps> = (props: IInsightsTableProps) => {

    const [columns] = useState([
        createTableColumn<INotification>({
            columnId: "sentDate",
            renderHeaderCell: () => <>Sent Date</>,
            compare: (a, b) => {
                return moment(a.sentDate).isBefore(b.sentDate) ? 1 : -1;
            },
        }),
        createTableColumn<INotification>({
            columnId: "title",
            renderHeaderCell: () => <>Title</>,
            compare: (a, b) => {
                return a.title.localeCompare(b.title);
            },
        }),
        createTableColumn<INotification>({
            columnId: "sender",
            renderHeaderCell: () => <>Sender</>,
            compare: (a, b) => {
                return a.sender.localeCompare(b.sender);
            },
        }),
        createTableColumn<INotification>({
            columnId: "status",
            renderHeaderCell: () => <>Status</>,
            compare: (a, b) => {
                return a.status.localeCompare(b.status);
            },
        }),
        createTableColumn<INotification>({
            columnId: "totalRecipients",
            renderHeaderCell: () => <>Total Recipients</>,
            compare: (a, b) => {
                return a.totalMessageCount < b.totalMessageCount ? 1 : -1;
            },
        }),
        createTableColumn<INotification>({
            columnId: "succeeded",
            renderHeaderCell: () => <>Succeeded</>,
            compare: (a, b) => {
                return Number(a.succeeded) < Number(b.totalMessageCount) ? 1 : -1;
            },
        }),
        createTableColumn<INotification>({
            columnId: "failed",
            renderHeaderCell: () => <>Failed</>,
            compare: (a, b) => {
                return Number(a.failed) < Number(b.failed) ? 1 : -1;
            },
        }),
        createTableColumn<INotification>({
            columnId: "isImportant",
            renderHeaderCell: () => <>Is Important</>,
            compare: (a, b) => {
                return (a.isImportant === b.isImportant) ? 0 : a.isImportant ? 1 : -1;
            },
        }),
    ])
    const [columnSizingOptions] = React.useState<TableColumnSizingOptions>({
        sentDate: {
            minWidth: 100,
            defaultWidth: 190
        },
        title: {
            minWidth: 100,
            defaultWidth: 400,
        },
        sender: {
            minWidth: 100,
            defaultWidth: 190
        },
        status: {
            minWidth: 70,
            defaultWidth: 120
        },
        totalRecipients: {
            minWidth: 70,
            defaultWidth: 100
        },
        succeeded: {
            minWidth: 70,
            defaultWidth: 100
        },
        failed: {
            minWidth: 70,
            defaultWidth: 100
        },
        isImportant: {
            minWidth: 70,
            defaultWidth: 100
        },
    });


    const {
        getRows,
        sort: { getSortDirection, toggleColumnSort, sort },
        columnSizing_unstable,
        tableRef,
        selection: { toggleRow, isRowSelected },
    } = useTableFeatures(
        {
            columns,
            items: props.items
        },
        [
            useTableSort({}),
            useTableColumnSizing_unstable({ columnSizingOptions }),
            useTableSelection({
                selectionMode: "single"
            })
        ]
    );

    const headerSortProps = (columnId: TableColumnId) => ({
        onClick: (e: React.MouseEvent) => {
            toggleColumnSort(e, columnId);
        },
        sortDirection: getSortDirection(columnId),
    });

    const rows = sort(getRows((row) => {
        const selected = isRowSelected(row.rowId);
        return {
            ...row,
            onClick: async (e: React.MouseEvent) => {
                if (selected) {
                    props.onDbClick(row.item);
                } else {
                    toggleRow(e, row.rowId);
                    await props.onClick(row.item)
                }
            },
            onKeyDown: async (e: React.KeyboardEvent) => {
                if (e.key === " ") {
                    e.preventDefault();
                    toggleRow(e, row.rowId);
                    await props.onClick(row.item)
                } if (e.key === "Enter") {
                    e.preventDefault();
                    props.onDbClick(row.item);
                }
            },
            selected,
            appearance: selected ? ("brand" as const) : ("none" as const),
        };
    }));

    return (
        <Table sortable aria-label="Insights table" ref={tableRef}>
            <TableHeader>
                <TableRow>
                    <TableSelectionCell type="radio" hidden />
                    {columns.map((column) => (
                        <Menu openOnContext key={column.columnId}>
                            <MenuTrigger>
                                <TableHeaderCell
                                    key={column.columnId}
                                    {...columnSizing_unstable.getTableHeaderCellProps(
                                        column.columnId
                                    )}
                                    {...headerSortProps(column.columnId)}
                                >
                                    <span className='header-cell-text'>{column.renderHeaderCell()}</span>
                                </TableHeaderCell>
                            </MenuTrigger>
                            <MenuPopover>
                                <MenuList>
                                    <MenuItem
                                        onClick={columnSizing_unstable.enableKeyboardMode(
                                            column.columnId
                                        )}
                                    >
                                        Keyboard Column Resizing
                                    </MenuItem>
                                </MenuList>
                            </MenuPopover>
                        </Menu>
                    ))}
                </TableRow>
            </TableHeader>
            <TableBody>
                {rows.map(({ item, selected, onClick, onKeyDown, appearance }) => (
                    <TableRow
                        key={item.id}
                        onClick={onClick}
                        onKeyDown={onKeyDown}
                        aria-selected={selected}
                        appearance={appearance}>
                        <TableSelectionCell
                            checked={selected}
                            type="radio"
                            radioIndicator={{ "aria-label": "Select row" }}
                        />
                        <TableCell {...columnSizing_unstable.getTableCellProps("sentDate")}>
                            <TableCellLayout truncate>
                                {item.sentDate}
                            </TableCellLayout>
                        </TableCell>
                        <TableCell {...columnSizing_unstable.getTableCellProps("title")}>
                            <TableCellLayout truncate>
                                {item.title}
                            </TableCellLayout>
                        </TableCell>
                        <TableCell {...columnSizing_unstable.getTableCellProps("sender")}>
                            <TableCellLayout truncate>
                                {item.sender}
                            </TableCellLayout>
                        </TableCell>
                        <TableCell {...columnSizing_unstable.getTableCellProps("status")}>
                            <TableCellLayout truncate>
                                {item.status}
                            </TableCellLayout>
                        </TableCell>
                        <TableCell {...columnSizing_unstable.getTableCellProps("totalRecipients")}>
                            <TableCellLayout truncate>
                                {item.totalMessageCount.toString()}
                            </TableCellLayout>
                        </TableCell>
                        <TableCell {...columnSizing_unstable.getTableCellProps("succeeded")}>
                            <TableCellLayout truncate>
                                {item.succeeded.toString()}
                            </TableCellLayout>
                        </TableCell>
                        <TableCell {...columnSizing_unstable.getTableCellProps("failed")}>
                            <TableCellLayout truncate>
                                {item.failed.toString()}
                            </TableCellLayout>
                        </TableCell>
                        <TableCell {...columnSizing_unstable.getTableCellProps("isImportant")}>
                            <TableCellLayout truncate>
                                {item.isImportant.toString()}
                            </TableCellLayout>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>)
}

export default InsightsTable